declare const grecaptcha: {
  enterprise: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
  };
};

/** Why a token could not be generated in the browser. Mirrored by the backend allowlist. */
export type RecaptchaTokenErrorReason = 'script-not-loaded' | 'execute-failed';

export class RecaptchaTokenError extends Error {
  constructor(
    public readonly reason: RecaptchaTokenErrorReason,
    options?: { cause?: unknown }
  ) {
    super(`reCAPTCHA token generation failed: ${reason}`, options);
    this.name = 'RecaptchaTokenError';
  }
}

let cachedFailureReason: RecaptchaTokenErrorReason | null = null;

// The reCAPTCHA script loads with async+defer, so it may not have set up the
// grecaptcha global by the time mounted() fires. Poll briefly before giving up.
function waitForGrecaptcha(timeoutMs = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof grecaptcha !== 'undefined' && grecaptcha.enterprise) {
      return resolve();
    }
    const deadline = Date.now() + timeoutMs;
    const poll = setInterval(() => {
      if (typeof grecaptcha !== 'undefined' && grecaptcha.enterprise) {
        clearInterval(poll);
        resolve();
      } else if (Date.now() > deadline) {
        clearInterval(poll);
        reject(new Error('reCAPTCHA script not loaded'));
      }
    }, 100);
  });
}

export default function useRecaptcha() {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string;

  /**
   * Rejects with a {@link RecaptchaTokenError} when no token can be produced. Callers decide what
   * that means — the fail-open policy lives at the call site, not here.
   */
  const getToken = async (action: string): Promise<string> => {
    if (cachedFailureReason) {
      throw new RecaptchaTokenError(cachedFailureReason);
    }

    try {
      await waitForGrecaptcha();
    } catch (err) {
      cachedFailureReason = 'script-not-loaded';
      throw new RecaptchaTokenError(cachedFailureReason, { cause: err });
    }

    try {
      const token = await new Promise<string>((resolve, reject) => {
        grecaptcha.enterprise.ready(async () => {
          try {
            resolve(await grecaptcha.enterprise.execute(siteKey, { action }));
          } catch (err) {
            reject(err);
          }
        });
      });

      // Upholds the invariant the guard now enforces: an empty token is only ever sent together
      // with the error header. Google documents execute() as resolving a token string, but
      // nothing stops it resolving a blank one — treat that as a failure rather than pass it on.
      if (!token) {
        throw new Error('reCAPTCHA resolved an empty token');
      }
      return token;
    } catch (err) {
      cachedFailureReason = 'execute-failed';
      throw new RecaptchaTokenError(cachedFailureReason, { cause: err });
    }
  };

  return { getToken };
}
